// Tipos del "backend hardcodeado" del MVP.
// El empleado reutiliza el tipo canónico de @fycom/shared-types para que la
// lista de empleados y la edición sean type-safe sin duplicar definiciones.
import type { Employee, EmployeeRole } from '@fycom/shared-types';

export type { Employee, EmployeeRole };

// `isActive` (boolean) sigue siendo la fuente de verdad para el reporte:
// activo → aparece; cualquier inactivo → no aparece.
//
// `inactiveReason` es solo una TAG informativa que distingue el TIPO de inactivo
// cuando isActive=false. No afecta ningún cálculo.
//   - 'inactivo': desvinculado, probablemente no vuelve.
//   - 'pausado_estudios' / 'licencia_maternal': pausa temporal, volverá.
export type InactiveReason =
  | 'inactivo'
  | 'pausado_estudios'
  | 'licencia_maternal';

// Etiquetas para mostrar en el selector de Estado.
export const ESTADO_LABELS: Record<'activo' | InactiveReason, string> = {
  activo: 'Activo',
  inactivo: 'Inactivo',
  pausado_estudios: 'Pausado por Estudios',
  licencia_maternal: 'Licencia Maternal',
};

// Jornada del colaborador. Define las HORAS META por día hábil (valores fijos):
//   - 'full_time' → 7 hrs/día
//   - 'part_time' → 4 hrs/día
// Esa misma meta se usa como denominador para AMBOS cumplimientos:
//   Cumplimiento Total     = horas trabajadas (todas)   / meta
//   Cumplimiento Facturado = horas facturables (billable)/ meta
// A futuro se puede agregar 'otro' con un valor específico por persona.
export type Jornada = 'full_time' | 'part_time';

export const JORNADA_LABELS: Record<Jornada, string> = {
  full_time: 'Full-Time',
  part_time: 'Part-Time',
};

// Horas meta por día hábil según jornada. EDITAR ACÁ para cambiar las metas.
export const JORNADA_HORAS: Record<Jornada, number> = {
  full_time: 7,
  part_time: 4,
};

// SeedEmployee = Employee + tags locales (no afectan shared-types).
//   - inactiveReason: tipo de inactividad (solo informativo).
//   - jornada: full/part time. Si falta, se asume full_time.
export type SeedEmployee = Employee & {
  inactiveReason?: InactiveReason;
  jornada?: Jornada;
};

// Registro de horas (fuente: Lemon Time Billing), acotado a 02-03 → 30-05-2026.
// Datos crudos en seed-time-entries.json (importado por el store).
export interface SeedTimeEntry {
  employeeId: string;
  date: string; // YYYY-MM-DD (fecha del trabajo)
  client: string; // Cliente
  matter: string; // Asunto
  description: string; // Descripción (puede ser multilínea)
  durationHours: number; // Duración (horas decimales)
  chargedHours: number; // Duración cobrada (horas decimales)
  billable: boolean; // Cobrable (SI/NO)
  currency: string; // UF | USD | CLP
  rate: number; // Tarifa HH
  value: number; // Valor Trabajo
  entryDate: string; // Fecha de Ingreso (YYYY-MM-DD)
}

export interface SeedAbsence {
  employeeId: string;
  type: string; // 'vacation' | 'medical' | ...
  startDate: string;
  endDate: string;
  days: number;
}

export interface SeedAdminDay {
  employeeId: string;
  date: string;
  quantity: number;
}

// ---- Tipos de salida del reporte (lo que consume el frontend) ----

export interface EmployeeReport {
  employeeId: string;
  displayName: string;
  legalName: string;
  rut: string | null;
  area: string | null;
  // subArea: para la card "Procuradores", el área real del procurador
  // (Corporativo / Tributarios). Para el resto es igual a `area`.
  subArea: string | null;
  role: string;
  company: string;
  targetBillablePct: number;
  expectedTotalHours: number;
  expectedBillableHours: number;
  actualTotalHours: number;
  actualBillableHours: number;
  valueUF: number; // suma del Valor Trabajo (UF) de los registros facturables del período
  pctTotal: number;
  pctBillable: number;
}

export interface AreaReport {
  area: string;
  employees: EmployeeReport[];
  expectedTotalHours: number;
  expectedBillableHours: number;
  actualTotalHours: number;
  actualBillableHours: number;
  valueUF: number; // suma de valueUF de todos los empleados del área
  pctTotal: number;
  pctBillable: number;
  // Header "Cumple / No Cumple": % de personas del área que cumplen el umbral.
  // Se cuentan por separado Total y Facturables.
  cumpleTotalPct: number; // % de personas con pctTotal ≥ umbral
  cumpleBillablePct: number; // % de personas con pctBillable ≥ umbral
}

export interface PeriodReport {
  startDate: string;
  endDate: string;
  areas: AreaReport[];
}
